import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Trash2 } from "lucide-react";
import {
  manufacturerService,
  Manufacturer,
} from "../../services/manufacturer.service";
import { showToast } from "../../lib/toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

export function ViewManufacturerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [manufacturer, setManufacturer] = useState<Manufacturer | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadManufacturer();
    }
  }, [id]);

  const loadManufacturer = async () => {
    try {
      setLoading(true);
      const data = await manufacturerService.getById(id!);
      setManufacturer(data);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load manufacturer",
      );
      navigate("/manufacturers");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await manufacturerService.delete(id!);
      showToast.success("Manufacturer deleted successfully");
      navigate("/manufacturers");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to delete manufacturer",
      );
    } finally {
      setIsDeleting(false);
      setDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  if (!manufacturer) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Manufacturer not found</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/manufacturers")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Manufacturers
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {manufacturer.manufacturerName}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Code: {manufacturer.manufacturerCode}
              {manufacturer.shortName && ` (${manufacturer.shortName})`}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/manufacturers/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Manufacturer Code</span>
              <span className="font-medium">
                {manufacturer.manufacturerCode}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Manufacturer Name</span>
              <span className="font-medium">
                {manufacturer.manufacturerName}
              </span>
            </div>
            {manufacturer.shortName && (
              <div className="flex justify-between">
                <span className="text-gray-500">Short Name</span>
                <span className="font-medium">{manufacturer.shortName}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  manufacturer.isActive
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {manufacturer.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Products</span>
              <span className="font-medium">
                {manufacturer._count?.products || 0}
              </span>
            </div>
          </div>
        </div>

        {/* License Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">License Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">License Number</span>
              <span className="font-medium">
                {manufacturer.licenseNumber || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">License Expiry</span>
              <span className="font-medium">
                {manufacturer.licenseExpiry
                  ? new Date(manufacturer.licenseExpiry).toLocaleDateString()
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">GST Number</span>
              <span className="font-medium">
                {manufacturer.gstNumber || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">PAN Number</span>
              <span className="font-medium">
                {manufacturer.panNumber || "-"}
              </span>
            </div>
            {manufacturer.qualityRating && (
              <div className="flex justify-between">
                <span className="text-gray-500">Quality Rating</span>
                <span className="font-medium">
                  {manufacturer.qualityRating}/5
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Phone</span>
              <span className="font-medium">{manufacturer.phone || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Email</span>
              <span className="font-medium">{manufacturer.email || "-"}</span>
            </div>
            {manufacturer.website && (
              <div className="flex justify-between">
                <span className="text-gray-500">Website</span>
                <a
                  href={manufacturer.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {manufacturer.website}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Address</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Address</span>
              <span className="font-medium text-right max-w-xs">
                {manufacturer.address || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">City</span>
              <span className="font-medium">{manufacturer.city || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">State</span>
              <span className="font-medium">{manufacturer.state || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Pincode</span>
              <span className="font-medium">{manufacturer.pincode || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Country</span>
              <span className="font-medium">
                {manufacturer.country || "India"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Manufacturer"
        message={`Are you sure you want to delete "${manufacturer.manufacturerName}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default ViewManufacturerPage;
